import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, ChevronRight, Phone } from "lucide-react";
import logo from "@/assets/logo-transparent.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { gtagReportConversion } from "@/lib/gtag";
const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const stayOptions = [
    { label: "Glamping Overview", href: "/glamping" },
    { label: "Camping Overview", href: "/camping" },
    { label: "Cabins Overview", href: "/cabins" },
    { label: "Safari Cabins", href: "/stay-overnight#safari-cabins" },
    { label: "Log Cabins", href: "/stay-overnight#log-cabins" },
    { label: "Luxury Glamping Cabins", href: "/stay-overnight#luxury-glamping-cabins" },
    { label: "Glamping Cabins", href: "/stay-overnight#glamping-cabins" },
    { label: "Bell Tents", href: "/stay-overnight#bell-tents" },
    { label: "Tent Spots", href: "/stay-overnight#tent-spots" },
  ];

  const dayOptions = [
    { label: "Riverside Cabanas", href: "/day-rentals#cabanas" },
    { label: "Picnic Tables", href: "/day-rentals#picnic-tables" },
    { label: "All Cabana Rentals", href: "/cabana-rentals" },
    { label: "Day Passes", href: "/day-passes" },
    { label: "River Day Trip", href: "/day-trip" },
  ];

  const tubingKayakingOptions = [
    { label: "Tubing", href: "/day-rentals#tubing" },
    { label: "Kayaking", href: "/day-rentals#kayaking" },
  ];

  const planOptions = [
    { label: "FAQs", href: "/plan-your-visit#faqs" },
    { label: "What to Bring", href: "/plan-your-visit#what-to-bring" },
    { label: "Hours & Directions", href: "/plan-your-visit#hours-directions" },
    { label: "Contact Us", href: "/plan-your-visit#contact" },
  ];

  const aboutOptions = [
    { label: "Our Story", href: "/about" },
    { label: "Reviews", href: "/reviews" },
    { label: "Other Son's Locations", href: "/locations" },
    { label: "Blog", href: "/blog" },
  ];

  const propertyMapOptions = [
    { label: "Property Map", href: "/property-map#property-map" },
    { label: "Tubing Route", href: "/property-map#tubing-route" },
    { label: "Directions", href: "/property-map#directions" },
  ];

  return (
    <header
      className="fixed left-0 right-0 z-50 transition-[top] duration-300"
      style={{ top: "var(--promo-banner-height, 0px)" }}
    >
      <div className="bg-cream/95 backdrop-blur-md shadow-soft">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="Son's Blue River Camp" 
              className="h-14 sm:h-16 w-auto"
              width={116}
              height={64}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <div className="flex items-center">
              <Link to="/stay-overnight" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                Stay Overnight
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="Stay Overnight options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {stayOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center">
              <Link to="/day-rentals" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                Day Rentals
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="Day Rentals options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {dayOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center">
              <Link to="/day-rentals#tubing-kayaking" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                Tubing & Kayaking
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="Tubing and Kayaking options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {tubingKayakingOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center">
              <Link to="/property-map" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                Property Map
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="Property Map options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {propertyMapOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center">
              <Link to="/plan-your-visit" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                Plan Your Visit
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="Plan Your Visit options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {planOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center">
              <Link to="/about" className="px-3 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
                About
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 text-foreground hover:text-river transition-colors" aria-label="About options">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-cream border-border">
                  {aboutOptions.map((option) => (
                    <DropdownMenuItem key={option.href} asChild>
                      <Link to={option.href} className="cursor-pointer">{option.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Link to="/specials" className="px-4 py-2 text-sm font-body font-medium text-foreground hover:text-river transition-colors">
              Specials
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact" className="text-sm font-body font-medium text-foreground hover:text-river transition-colors">
              Contact
            </Link>
            <a href="tel:+18775777667" className="flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-river transition-colors">
              <Phone className="h-4 w-4" />
              (877) 577-7667
            </a>
            <Button variant="cta" size="lg" onClick={() => { gtagReportConversion(); navigate('/book-now'); }}>
              Book Now
            </Button>
          </div>

          {/* Mobile Book Now + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <Button variant="cta" size="sm" onClick={() => { gtagReportConversion(); navigate('/book-now'); }}>
              Book Now
            </Button>
            <button
              className="p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-cream border-t border-border animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-0">
            <div className="pb-4">
              <Link to="/" className="block py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            </div>
            <div className="border-t border-border pt-4 space-y-1 pb-4">
              <div className="flex items-center">
                <Link to="/stay-overnight" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Stay Overnight
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, stay: !prev.stay }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.stay ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.stay && stayOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-1 pb-4">
              <div className="flex items-center">
                <Link to="/day-rentals" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Day Rentals
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, day: !prev.day }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.day ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.day && dayOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-1 pb-4">
              <div className="flex items-center">
                <Link to="/day-rentals#tubing-kayaking" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Tubing & Kayaking
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, tubingKayaking: !prev.tubingKayaking }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.tubingKayaking ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.tubingKayaking && tubingKayakingOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-1 pb-4">
              <div className="flex items-center">
                <Link to="/property-map" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Property Map
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, propertyMap: !prev.propertyMap }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.propertyMap ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.propertyMap && propertyMapOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 pb-4">
              <Link to="/contact" className="block py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            </div>
            <div className="border-t border-border pt-4 space-y-1 pb-4">
              <div className="flex items-center">
                <Link to="/plan-your-visit" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Plan Your Visit
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, plan: !prev.plan }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.plan ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.plan && planOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-1">
              <div className="flex items-center">
                <Link to="/about" className="flex-1 py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  About
                </Link>
                <button 
                  onClick={() => setExpandedSections(prev => ({ ...prev, about: !prev.about }))}
                  className="p-2 text-foreground hover:text-river transition-colors"
                >
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedSections.about ? 'rotate-90' : ''}`} />
                </button>
              </div>
              {expandedSections.about && aboutOptions.map((option) => (
                <Link key={option.href} to={option.href} className="block py-2 pl-4 text-foreground hover:text-river transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {option.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border pt-4 pb-4">
              <Link to="/specials" className="block py-2 font-semibold text-foreground hover:text-river transition-colors" onClick={() => setMobileMenuOpen(false)}>Specials</Link>
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <a href="tel:+18775777667" className="flex items-center gap-2 py-2 text-foreground">
                <Phone className="h-4 w-4" />
                (877) 577-7667
              </a>
              <Button variant="cta" size="lg" className="w-full" onClick={() => { setMobileMenuOpen(false); gtagReportConversion(); navigate('/book-now'); }}>
                Book Now
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </header>
  );
};

export default Header;
